// src/pages/rules/RuleFormModal.jsx
//
// The create/edit Modal for a single rule -- scope/account picker
// (create only), the rule fields, and factor-based weight scoring. Split
// out of RulesConfigPage.jsx.

import PropTypes from "prop-types";
import { Alert, Button, FormField, Input, Modal, Select } from "../../shared/ui";
import { SEVERITY_OPTIONS } from "./ruleOptions";

export default function RuleFormModal({
  open,
  editingId,
  canCreateAnything,
  onClose,
  onSubmit,
  saving,

  form,
  onChange,

  scope,
  onScopeChange,
  scopeSelectOptions,
  scopeAccountId,
  onScopeAccountChange,
  selectableAccounts,
  accountNameById,

  typeMode,
  customType,
  contractTypeSelectValue,
  contractTypeOptions,
  onContractTypeSelectChange,
  onCustomTypeChange,

  categoryMode,
  categoryCustom,
  categorySelectValue,
  categoryOptions,
  onCategorySelectChange,
  onCategoryCustomChange,

  dimensionMode,
  dimensionCustom,
  dimensionSelectValue,
  dimensionOptions,
  onDimensionSelectChange,
  onDimensionCustomChange,

  computedWeight,
  normalizedWeight,
}) {
  const canSubmit = Boolean(editingId) || canCreateAnything;

  return (
    <Modal
      open={open}
      title={editingId ? "Edit Rule" : "Add New Rule"}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {canSubmit ? "Cancel" : "Close"}
          </Button>
          {canSubmit && (
            <Button type="submit" form="rule-form" loading={saving}>
              {editingId ? "Update Rule" : "Create Rule"}
            </Button>
          )}
        </>
      }
    >
      {open && !canSubmit && (
        <Alert variant="warning">
          You can view the rule catalog but don't have permission to create rules anywhere.
        </Alert>
      )}
      {open && canSubmit && (
        <form id="rule-form" onSubmit={onSubmit} className="flex flex-col gap-4">
          <FormField label="Scope">
            {editingId ? (
              <p className="shui-input bg-gray-50">
                {form.org_id == null
                  ? "Global"
                  : form.account_id == null
                  ? "Org-wide"
                  : `Account — ${accountNameById.get(form.account_id) || `#${form.account_id}`}`}
              </p>
            ) : (
              <Select options={scopeSelectOptions} value={scope} onChange={onScopeChange} />
            )}
          </FormField>
          {!editingId && scope === "account" && (
            <FormField label="Account" required>
              <Select
                options={[
                  { value: "", label: "Select an account…" },
                  ...selectableAccounts.map((a) => ({
                    value: String(a.account_id),
                    label: a.name,
                  })),
                ]}
                value={scopeAccountId}
                onChange={onScopeAccountChange}
                required
              />
            </FormField>
          )}

          <FormField label="Name (Rule Key)" required>
            <Input
              name="name"
              value={form.name}
              onChange={onChange}
              required
              placeholder="e.g. TM_SCOPE_BOUNDARY, FB_ACCEPTANCE_CRITERIA"
            />
          </FormField>

          <FormField label="Contract Type">
            <Select
              options={contractTypeOptions}
              value={contractTypeSelectValue}
              onChange={onContractTypeSelectChange}
            />
          </FormField>
          {typeMode === "custom" && (
            <FormField label="Custom Type">
              <Input
                value={customType}
                onChange={onCustomTypeChange}
                placeholder="e.g. Outcome-Based"
              />
            </FormField>
          )}

          <FormField label="Category">
            <Select
              options={categoryOptions}
              value={categorySelectValue}
              onChange={onCategorySelectChange}
            />
          </FormField>
          {categoryMode === "custom" && (
            <FormField label="Custom Category">
              <Input value={categoryCustom} onChange={onCategoryCustomChange} />
            </FormField>
          )}

          <FormField label="Dimension">
            <Select
              options={dimensionOptions}
              value={dimensionSelectValue}
              onChange={onDimensionSelectChange}
            />
          </FormField>
          {dimensionMode === "custom" && (
            <FormField label="Custom Dimension">
              <Input value={dimensionCustom} onChange={onDimensionCustomChange} />
            </FormField>
          )}

          <FormField label="Description">
            <textarea
              name="description"
              value={form.description}
              onChange={onChange}
              rows={3}
              className="shui-input"
            />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Severity">
              <Select
                options={SEVERITY_OPTIONS}
                name="severity"
                value={form.severity}
                onChange={onChange}
              />
            </FormField>
            <label className="flex items-center gap-2 text-sm text-slate-700 self-end pb-2.5">
              <input
                type="checkbox"
                name="auto_weight"
                checked={!!form.auto_weight}
                onChange={onChange}
                className="w-4 h-4 accent-blue-600"
              />
              Auto-calc weight from factors
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Impact (1–4)">
              <Input
                type="number"
                name="impact"
                min="1"
                max="4"
                step="1"
                value={form.impact}
                onChange={onChange}
                disabled={!!form.auto_weight}
              />
            </FormField>
            <FormField label="Likelihood (1–4)">
              <Input
                type="number"
                name="likelihood"
                min="1"
                max="4"
                step="1"
                value={form.likelihood}
                onChange={onChange}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Recoverability (1–4)">
              <Input
                type="number"
                name="recoverability"
                min="1"
                max="4"
                step="1"
                value={form.recoverability}
                onChange={onChange}
              />
            </FormField>
            <FormField label="Applicability (1–4)">
              <Input
                type="number"
                name="applicability"
                min="1"
                max="4"
                step="1"
                value={form.applicability}
                onChange={onChange}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Weight (stored)">
              <Input
                type="number"
                name="weight"
                value={form.auto_weight ? computedWeight : form.weight}
                onChange={onChange}
                disabled={!!form.auto_weight}
              />
            </FormField>
            <FormField label="Computed (raw / normalized)">
              <Input value={`${computedWeight}  /  ${normalizedWeight}`} readOnly />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Min Score (optional)">
              <Input
                type="number"
                name="min_score"
                value={form.min_score}
                onChange={onChange}
                min="0"
                max="100"
              />
            </FormField>
            <FormField label="Max Score (optional)">
              <Input
                type="number"
                name="max_score"
                value={form.max_score}
                onChange={onChange}
                min="0"
                max="100"
              />
            </FormField>
          </div>

          <FormField label="Keyword Pattern (optional)">
            <Input name="keyword_pattern" value={form.keyword_pattern} onChange={onChange} />
          </FormField>

          <FormField label="Prompt Hint (optional)">
            <textarea
              name="prompt_hint"
              value={form.prompt_hint}
              onChange={onChange}
              rows={3}
              className="shui-input"
            />
          </FormField>

          <div className="flex items-center gap-5">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                name="is_mandatory"
                checked={form.is_mandatory}
                onChange={onChange}
                className="w-4 h-4 accent-blue-600"
              />
              Mandatory rule
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                name="active"
                checked={form.active}
                onChange={onChange}
                className="w-4 h-4 accent-blue-600"
              />
              Active
            </label>
          </div>
        </form>
      )}
    </Modal>
  );
}

RuleFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  editingId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canCreateAnything: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  form: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  scope: PropTypes.string.isRequired,
  onScopeChange: PropTypes.func.isRequired,
  scopeSelectOptions: PropTypes.array.isRequired,
  scopeAccountId: PropTypes.string.isRequired,
  onScopeAccountChange: PropTypes.func.isRequired,
  selectableAccounts: PropTypes.array.isRequired,
  accountNameById: PropTypes.instanceOf(Map).isRequired,
  typeMode: PropTypes.string.isRequired,
  customType: PropTypes.string.isRequired,
  contractTypeSelectValue: PropTypes.string.isRequired,
  contractTypeOptions: PropTypes.array.isRequired,
  onContractTypeSelectChange: PropTypes.func.isRequired,
  onCustomTypeChange: PropTypes.func.isRequired,
  categoryMode: PropTypes.string.isRequired,
  categoryCustom: PropTypes.string.isRequired,
  categorySelectValue: PropTypes.string.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  onCategorySelectChange: PropTypes.func.isRequired,
  onCategoryCustomChange: PropTypes.func.isRequired,
  dimensionMode: PropTypes.string.isRequired,
  dimensionCustom: PropTypes.string.isRequired,
  dimensionSelectValue: PropTypes.string.isRequired,
  dimensionOptions: PropTypes.array.isRequired,
  onDimensionSelectChange: PropTypes.func.isRequired,
  onDimensionCustomChange: PropTypes.func.isRequired,
  computedWeight: PropTypes.number.isRequired,
  normalizedWeight: PropTypes.string.isRequired,
};
